import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { Practitioner, Reference } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import * as Sentry from "@sentry/react";

import { joinReferenceString } from "@/utils/medplum";
import { showSystemErrorNotification, toast } from "@/utils/notifications";
import { readBrowserStoredOrganizationId } from "@/utils/read-browser-stored-organization-id";

import { FEE_SCHEDULE_LOAD_ERROR_TOAST_ID } from "../constants/fee-table";
import {
  fetchProcedureFeeQuestionnaire,
  fetchProcedureFeeQuestionnaireResponse,
  fetchProcedureFeeResponsesByCode,
  loadProcedureCodesFromValueSet,
  parseProcedureFeeQuestionnaireResponse,
  saveProcedureFeeQuestionnaireResponse,
} from "../services/procedure-fees";
import { ProcedureCode } from "../types/fee-table";
import { EditFeesFormType } from "../zod-schemas/edit-fees";

/**
 * Custom hook for managing procedure fees from a ValueSet
 * Handles loading procedure codes, merging with Questionnaire responses, and saving updates
 */
export function useProcedureFees(valueSetUrl: string, refreshKey = 0) {
  const medplum = useMedplum();
  const { practiceId } = useParams<{ practiceId: string }>();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const [procedures, setProcedures] = useState<ProcedureCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  /** Set after mount so first client render matches SSR (no localStorage during render). */
  const [organizationIdFromStorage, setOrganizationIdFromStorage] = useState<string | undefined>(
    undefined,
  );

  const practitionerReference = useMemo<Reference<Practitioner> | undefined>(() => {
    if (!profile?.id) return undefined;
    return { reference: joinReferenceString("Practitioner", profile.id) };
  }, [profile]);

  useEffect(() => {
    setOrganizationIdFromStorage(readBrowserStoredOrganizationId());
  }, []);

  const activeOrganizationId = useMemo(() => {
    if (practiceId) return practiceId;
    return organizationIdFromStorage;
  }, [practiceId, organizationIdFromStorage]);

  const organizationReference = useMemo(
    () =>
      activeOrganizationId ? joinReferenceString("Organization", activeOrganizationId) : undefined,
    [activeOrganizationId],
  );

  type LoadProcedureFeesOptions = { showLoading?: boolean };

  // Load procedure codes from ValueSet and merge with fee data from QuestionnaireResponse
  const loadProcedureFees = useCallback(
    async (options?: LoadProcedureFeesOptions) => {
      const showLoading = options?.showLoading ?? true;

      if (!practitionerReference && !organizationReference) {
        // Always clear: we are not loading; avoids a stuck spinner if a prior call left isLoading true.
        setIsLoading(false);
        return;
      }

      try {
        if (showLoading) {
          setIsLoading(true);
        }
        // Invalidate search cache to ensure we get the latest QuestionnaireResponses
        medplum.invalidateSearches("QuestionnaireResponse");

        // Load procedure codes from ValueSet
        const procedureCodes = await loadProcedureCodesFromValueSet(medplum, valueSetUrl);

        // If no procedure codes found, surface via throw; caller shows one notification.
        if (procedureCodes.length === 0) {
          console.error("No procedure codes found in ValueSet");
          setProcedures([]);
          throw new Error("No procedure codes found in ValueSet");
        }

        const questionnaire = await fetchProcedureFeeQuestionnaire(medplum);
        let proceduresWithFees: ProcedureCode[];

        if (!questionnaire) {
          // If questionnaire doesn't exist yet, use procedure codes from ValueSet with default values
          proceduresWithFees = procedureCodes;
        } else {
          // Fetch all QuestionnaireResponses at once (more efficient than N searches)
          const responsesByCode = await fetchProcedureFeeResponsesByCode(
            medplum,
            questionnaire,
            practitionerReference,
            organizationReference,
          );

          // Map procedure codes to enriched data
          proceduresWithFees = procedureCodes.map((procedure) => {
            const questionnaireResponse = responsesByCode[procedure.code];
            return parseProcedureFeeQuestionnaireResponse(questionnaireResponse, procedure);
          });
        }

        setProcedures(proceduresWithFees);
      } catch (error) {
        // Show empty state on error; notification is owned by callers (initial load vs save refresh).
        setProcedures([]);
        throw new Error("Error loading procedure fees", { cause: error });
      } finally {
        if (showLoading) {
          setIsLoading(false);
        }
      }
    },
    [medplum, practitionerReference, valueSetUrl, organizationReference],
  );

  useEffect(() => {
    loadProcedureFees().catch((error: unknown) => {
      // The table is cleared on a failed read, and an empty table looks exactly like a
      // schedule nobody has filled in. Say which one it is, and report it, so a biller does
      // not read "no fees configured" off a table that simply never loaded.
      Sentry.captureException(error);
      toast({
        type: "error",
        // A stable id keeps a repeated failure updating this one notification. Without it
        // `toast` clears every other active notification on its way in.
        id: FEE_SCHEDULE_LOAD_ERROR_TOAST_ID,
        title: "Failed to load fee schedule",
        message:
          "This practice's saved fees could not be read, so the table is empty rather than unconfigured. Refresh the page before editing or quoting a fee.",
        autoClose: false,
      });
    });
  }, [loadProcedureFees, refreshKey]);

  // Shared async save logic for both handleSave and handleStatusToggle
  const upsertProcedureFee = useCallback(
    async (procedure: ProcedureCode, values: EditFeesFormType, onSuccess?: () => void) => {
      if (!practitionerReference || !practiceId) {
        toast({
          type: "error",
          title: "Cannot save fees",
          message: practitionerReference
            ? "Practice context is missing. Open Fees from a practice settings route (URL should include the practice id)."
            : "Your practitioner profile is not loaded. Refresh the page or sign in again.",
          autoClose: false,
        });
        return;
      }

      try {
        const questionnaire = await fetchProcedureFeeQuestionnaire(medplum);
        if (!questionnaire) {
          throw new Error("Procedure fee questionnaire not found");
        }

        // Invalidate cache before fetching to ensure we get latest data
        medplum.invalidateSearches("QuestionnaireResponse");

        // Fetch the practice-scoped QuestionnaireResponse when one exists (any author).
        const existingQuestionnaireResponse = await fetchProcedureFeeQuestionnaireResponse(
          medplum,
          procedure.code,
          questionnaire,
          organizationReference ? undefined : practitionerReference,
          organizationReference,
        );

        await saveProcedureFeeQuestionnaireResponse(
          medplum,
          questionnaire,
          procedure,
          values,
          practitionerReference,
          practiceId,
          existingQuestionnaireResponse,
          activeOrganizationId,
        );

        await loadProcedureFees({ showLoading: false });
        onSuccess?.();
      } catch (error) {
        showSystemErrorNotification();
        console.error("Error saving procedure fees:", error);
        throw error; // allow caller to react (e.g. revert UI)
      }
    },
    [
      medplum,
      practiceId,
      practitionerReference,
      loadProcedureFees,
      organizationReference,
      activeOrganizationId,
    ],
  );

  return {
    procedures,
    setProcedures,
    isLoading,
    loadProcedureFees,
    upsertProcedureFee,
  };
}
